<template>
  <div>
    <header
      class="parts-page__header d-flex align-center flex-wrap ga-4 mb-6"
    >
      <div class="flex-grow-1 min-width-0">
        <h1 class="parts-page__title text-h4 font-weight-bold mb-1">
          {{ t(strings.partsInventory) }}
        </h1>
        <p class="parts-page__subtitle text-body-1 text-medium-emphasis mb-0">
          {{ t(strings.page_subtitle) }}
        </p>
      </div>
      <v-btn
        :to="appLinks.sellPart.path"
        color="primary"
        variant="flat"
        size="large"
        class="text-none font-weight-semibold flex-shrink-0"
      >
        <v-icon start>mdi-plus</v-icon>
        {{ t(strings.sellAPart) }}
      </v-btn>
    </header>

    <v-row v-if="isAdmin">
      <v-col cols="auto">
        <v-btn
          color="primary"
          variant="flat"
          class="mb-4"
          :title="t(strings.createPart)"
          @click="onCreatePart"
        >
          <v-icon start>mdi-plus</v-icon>
          {{ t(strings.createPart) }}
        </v-btn>
      </v-col>
      <!-- <v-col cols="auto">
        <v-btn
          color="primary"
          variant="flat"
          class="mb-4"
          @click="onOpenImportExport"
        >
          <v-icon start>mdi-import</v-icon>
          Import/Export
        </v-btn>
      </v-col> -->
    </v-row>

    <PartsTable
      :parts="parts"
      :loading="partsQuery.isLoading.value"
      :error="partsQuery.error.value || undefined"
      :is-admin="isAdmin"
      @view="onViewPart"
      @express-interest="onExpressInterest"
    />

    <!-- Create Part Dialog -->
    <v-dialog v-model="showCreateDialog" max-width="600px" persistent>
      <PartForm @success="onPartCreated" @cancel="onCreateCancelled" />
    </v-dialog>

    <!-- Express Interest -->
    <v-bottom-sheet v-model="showInterestDialog" persistent height="90dvh">
      <PartInterestForm
        v-if="selectedPartForInterest"
        :part="selectedPartForInterest"
        :part-files="selectedPartFiles"
        @success="onInterestSubmitted"
        @cancel="closeInterestDialog"
      />
    </v-bottom-sheet>

    <!-- Import/Export Dialog -->
    <v-dialog v-model="showImportExportModal" max-width="1000px">
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <span>Import/Export Parts</span>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="onCloseImportExport"
          ></v-btn>
        </v-card-title>
        <v-card-text>
          <ImportExportCard :sources="[]" :loading="false" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { parts_page as strings } from "./PartsPage.strings.ts";
import { usePartsPageLoader } from "./PartsPage.loader.ts";
import { useReverseT } from "../../i18n.ts";
import {
  PartsTable,
  PartForm,
  PartInterestForm,
  ImportExportCard,
} from "@vendata/power-up-sdk/components";
import type { Part } from "@vendata/power-up-spec";
import { ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { identifyToPostHog } from "@vendata/posthog-client";
import { toValue } from "vue";
import { isAdminFromSession } from "@vendata/power-up-clients-common";
import { appLinks } from "@vendata/power-up-links";

const { t } = useReverseT();

const route = useRoute();
const router = useRouter();

const { partsQuery, sessionQuery } = usePartsPageLoader();

const session = sessionQuery.data.value;
if (session) {
  identifyToPostHog(toValue(session));
}

// Keep the reactive reference instead of extracting the value
const parts = computed(() => {
  if (!partsQuery.data.value) {
    return [];
  }
  return partsQuery.data.value.parts;
});

// Check if user is admin
const isAdmin = computed(() => (session ? isAdminFromSession(session) : false));

// Dialog state
const showCreateDialog = ref(false);

watch(
  () => route.query.createPart,
  (v) => {
    if (v === "1" || v === "true") {
      showCreateDialog.value = true;
      void router.replace({ path: route.path, query: {} });
    }
  },
  { immediate: true },
);

const showInterestDialog = ref(false);
const showImportExportModal = ref(false);
const selectedPartForInterest = ref<Part | null>(null);

// Event handlers
const onCreatePart = () => {
  showCreateDialog.value = true;
};

const onViewPart = (part: Part) => {
  void router.push({
    path: appLinks.partsPageDetail.path.replace(":id", part.id),
  });
};

const onPartCreated = (_createdPart: Part) => {
  showCreateDialog.value = false;
};

const onCreateCancelled = () => {
  showCreateDialog.value = false;
};

// Express interest dialog methods
const onExpressInterest = (part: Part) => {
  console.log("onExpressInterest", part);
  selectedPartForInterest.value = part;
  showInterestDialog.value = true;
};

const closeInterestDialog = () => {
  showInterestDialog.value = false;
  selectedPartForInterest.value = null;
};

const onInterestSubmitted = () => {
  closeInterestDialog();
  // Optionally show a success message or refresh data
};

// Import/Export modal handlers
// const onOpenImportExport = () => {
//   showImportExportModal.value = true;
// };

const onCloseImportExport = () => {
  showImportExportModal.value = false;
};

// Computed property for selected part files
const selectedPartFiles = computed(() => {
  if (!selectedPartForInterest.value) {
    return [];
  }
  // For now, return empty array - in a real implementation,
  // you would load the part files for the selected part
  return [];
});
</script>

<style scoped>
.parts-page__title {
  letter-spacing: -0.01em;
}

.min-width-0 {
  min-width: 0;
}
</style>
